/**
 * MyDossier — Section 5.5 + 5.6 of the architecture blueprint (wired in 5.6).
 *
 * Client-facing dossier checklist, reachable two ways:
 *   /dossier/{uuid}   — logged-in client/staff (session JWT auth)
 *   /dossier/{token}  — signed 24h deep link from a reminder email (no login;
 *                       the token is the auth, served by the by-link routes)
 *
 * Regions, top to bottom:
 *   1. Progress header                 (validated / in-review / needs-attention)
 *   2. Bulk drag-drop inbox            (presigned PUT, mode='bulk_inbox')
 *   3. Slot checklist                  (per-slot uploads, rejection reasons,
 *                                       re-upload supersedes a rejection)
 *   4. Mismatch banner                 (DECLARED_MISMATCH → confirm / re-label)
 *   5. "Help us identify these"        (LOW-confidence labeler → relabel)
 *
 * Live updates are polled (4s while anything is in-flight, 15s idle) —
 * EventBridge→AppSync push is deferred to the deploy-checkpoint session.
 *
 * Styling lives in MyDossier.css — self-contained, `.dos-`-prefixed.
 */

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
} from "react";
import { Link, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Camera,
  Check,
  Circle,
  FileText,
  Loader2,
  Upload,
  type LucideProps,
} from "lucide-react";

import type {
  Document,
  Dossier,
  DossierSlot,
  DossierSummary,
  PresignedUploadRequest,
} from "@re-platform/types";
import { slotActions } from "@re-platform/types";
import {
  ApiClientError,
  documentService,
  dossierService,
  formService,
  linkService,
  openDocument,
  uploadToPresignedUrl,
} from "@re-platform/api-client";
import {
  Button,
  Skeleton,
  StatusPill,
  Toast,
  type StatusTone,
} from "@re-platform/ui";

import { api } from "../../lib/api";

import "./MyDossier.css";

type Icon = ComponentType<LucideProps>;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const EXT_MIME: Record<string, string> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  tiff: "image/tiff",
  bmp: "image/bmp",
  heic: "image/heic",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

function mimeOf(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXT_MIME[ext] ?? "application/octet-stream";
}

interface SlotView {
  slot: DossierSlot;
  /** Derived display state — V017 statuses + "did a doc land yet". */
  state: "needed" | "processing" | "in_review" | "validated" | "rejected";
}

function slotState(slot: DossierSlot): SlotView["state"] {
  // V074 — a human approving counts exactly as validated to the client; they
  // do not need to know which of the two confirmed it.
  if (slot.status === "VALIDATED" || slot.status === "AGENT_APPROVED")
    return "validated";
  if (slot.status === "REJECTED") return "rejected";
  // Attached, but waiting on their agent to confirm it is the right document.
  if (slot.status === "AWAITING_APPROVAL") return "in_review";
  if (slot.status === "UPLOADED" || slot.status === "VALIDATING")
    return "in_review";
  return slot.document_id ? "processing" : "needed";
}

interface StateMeta {
  label: string;
  tone: StatusTone;
  accent: string;
  soft: string;
  Glyph: Icon;
  spin?: boolean;
}

const STATE_META: Record<SlotView["state"], StateMeta> = {
  needed: {
    label: "Needed",
    tone: "neutral",
    accent: "var(--rp-color-text-muted)",
    soft: "var(--rp-color-surface-2)",
    Glyph: Circle,
  },
  processing: {
    label: "Processing",
    tone: "info",
    accent: "var(--rp-color-info)",
    soft: "var(--rp-color-info-faint)",
    Glyph: Loader2,
    spin: true,
  },
  in_review: {
    label: "In review",
    tone: "info",
    accent: "var(--rp-color-info)",
    soft: "var(--rp-color-info-faint)",
    Glyph: Loader2,
    spin: true,
  },
  validated: {
    label: "Validated",
    tone: "success",
    accent: "var(--rp-color-success)",
    soft: "var(--rp-color-success-faint)",
    Glyph: Check,
  },
  rejected: {
    label: "Needs replacement",
    tone: "danger",
    accent: "var(--rp-color-danger)",
    soft: "var(--rp-color-danger-bg)",
    Glyph: AlertTriangle,
  },
};

/** Dossier slots whose document is a BLANK FORM the client fills in themselves.
 *
 *  Maps the slot's `doc_type_key` to the trec-svc form_type whose template holds
 *  the blank PDF. The server keeps its own allowlist (CLIENT_DOWNLOADABLE_FORMS)
 *  — this one only decides whether to render the button, and a mismatch degrades
 *  to a 404 rather than exposing anything. */
const BLANK_FORM_BY_DOC_TYPE: Record<string, string | undefined> = {
  sellers_disclosure: "sellers_disclosure",
};

export function MyDossier() {
  const { dossierId: param = "" } = useParams<{ dossierId: string }>();
  const linkMode = !UUID_RE.test(param);
  const queryClient = useQueryClient();

  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  //: Document id currently being opened — drives the per-row "Opening…" label.
  const [viewingDocId, setViewingDocId] = useState<string | null>(null);
  const [downloadingBlank, setDownloadingBlank] = useState<string | null>(null);
  const [pendingUploads, setPendingUploads] = useState<
    { name: string; status: "uploading" | "done" | "error" }[]
  >([]);

  const dossierQuery = useQuery({
    queryKey: ["dossier", param],
    queryFn: () =>
      linkMode
        ? linkService.resolve(api, param)
        : dossierService.get(api, param),
    refetchInterval: (query) =>
      hasInFlight(query.state.data) ? 4_000 : 15_000,
  });
  const dossier = dossierQuery.data;

  // All applications list — used for the switcher (session mode only).
  const allDossiersQuery = useQuery({
    queryKey: ["my-dossiers"],
    queryFn: () => dossierService.list(api, { limit: 50 }),
    enabled: !linkMode,
    staleTime: 30_000,
  });

  const documentsQuery = useQuery({
    queryKey: ["dossier-documents", param],
    queryFn: () =>
      linkMode
        ? linkService.listDocuments(api, param)
        : documentService.list(api, { client_id: dossier!.client_id }),
    enabled: linkMode || dossier !== undefined,
    refetchInterval: 8_000,
  });
  const documents = useMemo(
    () => documentsQuery.data ?? [],
    [documentsQuery.data],
  );

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["dossier", param] });
    void queryClient.invalidateQueries({
      queryKey: ["dossier-documents", param],
    });
  };

  // -- uploads ---------------------------------------------------------------

  const bulkInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const slotInputRef = useRef<HTMLInputElement>(null);
  const targetSlotRef = useRef<DossierSlot | null>(null);

  /** Open the blank PDF for a form the client fills in themselves. */
  async function downloadBlank(docTypeKey: string) {
    const formType = BLANK_FORM_BY_DOC_TYPE[docTypeKey];
    if (!formType) return;
    setDownloadingBlank(docTypeKey);
    try {
      await formService.openBlank(api, formType);
    } catch (err) {
      setToast(
        err instanceof Error
          ? err.message
          : "Couldn't open the blank form — please try again.",
      );
    } finally {
      setDownloadingBlank(null);
    }
  }

  async function presign(body: PresignedUploadRequest) {
    return linkMode
      ? linkService.presignedUrl(api, param, body)
      : documentService.presignedUrl(api, body);
  }

  async function uploadFiles(files: FileList | null, slot: DossierSlot | null) {
    if (!files || files.length === 0 || !dossier) return;
    const list = Array.from(files);
    setBusy(true);
    setPendingUploads(list.map((f) => ({ name: f.name, status: "uploading" })));
    // A 409 the server explained ("this document is required and the dossier is
    // complete"). Kept out of the per-file state because it replaces the whole
    // summary toast — retrying is not the advice.
    let refusal: string | null = null;
    try {
      // Upload all files in parallel — one failure never blocks the others.
      const results = await Promise.all(
        list.map(async (file, i) => {
          const body: PresignedUploadRequest = slot
            ? {
                mode: "per_slot",
                dossier_id: dossier.id,
                template_item_id: slot.template_item_id,
                doc_type_key: slot.doc_type_key,
                filename: file.name,
                mime_type: mimeOf(file),
                size_bytes: file.size,
              }
            : {
                mode: "bulk_inbox",
                ...(linkMode ? {} : { client_id: dossier.client_id }),
                filename: file.name,
                mime_type: mimeOf(file),
                size_bytes: file.size,
              };
          try {
            const presigned = await presign(body);
            await uploadToPresignedUrl(presigned, file);
            setPendingUploads((prev) =>
              prev.map((u, j) => (j === i ? { ...u, status: "done" } : u)),
            );
            return "ok" as const;
          } catch (err) {
            setPendingUploads((prev) =>
              prev.map((u, j) => (j === i ? { ...u, status: "error" } : u)),
            );
            // Keep the server's own words for a refusal. "Upload failed —
            // please try again" is actively wrong when the answer is "this
            // document is required and the dossier is complete": trying again
            // cannot work, and a client told only the generic line cannot tell
            // a bug from a rule.
            if (err instanceof ApiClientError && err.status === 409) {
              refusal = err.message;
            }
            return "error" as const;
          }
        }),
      );
      refresh();
      const errCount = results.filter((r) => r === "error").length;
      const okCount = results.length - errCount;
      if (errCount === 0) {
        setToast(
          slot
            ? `Got it — checking your ${slot.display_name} now.`
            : list.length === 1
              ? `Uploaded — we'll classify and slot it.`
              : `All ${list.length} files uploaded — we'll classify and slot them.`,
        );
      } else {
        setToast(
          refusal
            ? refusal
            : okCount === 0
              ? `Upload failed — please try again.`
              : `${okCount} of ${list.length} files uploaded. ${errCount} failed — try those again.`,
        );
      }
    } finally {
      setBusy(false);
      // Keep the status visible briefly so the user sees the outcome, then clear.
      setTimeout(() => setPendingUploads([]), 4000);
      for (const ref of [bulkInputRef, cameraInputRef, slotInputRef]) {
        if (ref.current) ref.current.value = "";
      }
    }
  }

  function pickForSlot(slot: DossierSlot) {
    targetSlotRef.current = slot;
    slotInputRef.current?.click();
  }

  /**
   * Open an uploaded file. The server hands back a short-lived presigned URL
   * and the browser fetches it straight from S3 (see `openDocument`).
   *
   * Session mode only. A signed dossier LINK is a shareable, emailed token
   * scoped to letting someone upload into a dossier — turning it into a way to
   * read documents back out is a different and much broader grant, so the
   * button is hidden rather than quietly 401-ing. If link-holders should be
   * able to read their own uploads, that wants its own by-link route and its
   * own decision.
   */
  async function viewDocument(documentId: string) {
    setViewingDocId(documentId);
    try {
      await openDocument(api, documentId);
    } catch (err) {
      setToast(
        err instanceof Error
          ? err.message
          : "Couldn't open that file just now.",
      );
    } finally {
      setViewingDocId(null);
    }
  }

  // -- labeler / mismatch actions ---------------------------------------------

  async function classify(
    documentId: string,
    action: "relabel" | "confirm_declared",
    docTypeKey?: string,
  ) {
    setBusy(true);
    try {
      const body = {
        action,
        ...(docTypeKey ? { doc_type_key: docTypeKey } : {}),
      };
      if (linkMode) await linkService.classify(api, param, documentId, body);
      else await documentService.classify(api, documentId, body);
      setToast(
        action === "relabel"
          ? "Thanks — re-checking it now."
          : "Thanks — confirmed.",
      );
      refresh();
    } catch (err) {
      setToast(
        err instanceof Error
          ? err.message
          : "That didn't work — please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * "I uploaded this by mistake" (spec 017).
   *
   * The labeller used to offer a document type and nothing else, and the
   * "doesn't look like a …" card offered no way out at all, so a client who
   * dropped the wrong file could only leave it there or deliberately mislabel
   * it. Now the document stops counting and leaves this page, and every item it
   * filled asks for the document again. Nothing is deleted server-side — the
   * agent keeps it as history.
   *
   * Not offered in link mode: a signed-link visitor is not necessarily the owner
   * of these documents, and destructive actions do not belong on a shared URL.
   */
  async function discard(documentId: string, filename: string) {
    if (
      !window.confirm(
        `Remove "${filename}"? It won't count toward your application. You can upload the right document afterwards.`,
      )
    )
      return;
    setBusy(true);
    try {
      await documentService.discard(api, documentId);
      setToast("Removed.");
      // Refetch rather than patch locally: the page shows what the server wrote
      // (it commits before answering, so the refetch sees it).
      refresh();
    } catch (err) {
      setToast(
        err instanceof Error
          ? err.message
          : "That didn't work — please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  // -- derived ----------------------------------------------------------------

  const slotViews: SlotView[] = (dossier?.slots ?? []).map((slot) => ({
    slot,
    state: slotState(slot),
  }));
  // Spec 019: may the client remove THIS document? Decided per document, not
  // per row — one filling an accepted item anywhere in the dossier is locked
  // on every row (and in the mismatch banner), exactly as the API refuses it.
  // The table is shared with the mock backend and document-svc's contract test.
  const removalLocked = (documentId: string): boolean =>
    (dossier?.slots ?? []).some(
      (s) =>
        s.document_id === documentId && !slotActions(s.status).client_remove,
    );
  const total = slotViews.length;
  const validated = slotViews.filter((v) => v.state === "validated").length;
  const inReview = slotViews.filter(
    (v) => v.state === "processing" || v.state === "in_review",
  ).length;
  const todo = total - validated - inReview;
  const pct = total > 0 ? Math.round((validated / total) * 100) : 0;

  // The dossier's own slot types are the labeler vocabulary — the registry
  // list lives behind admin-only routes.
  const typeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const { slot } of slotViews) {
      if (!seen.has(slot.doc_type_key))
        seen.set(slot.doc_type_key, slot.display_name);
    }
    return [...seen.entries()];
  }, [slotViews]);

  // ⚠️ `documents` is every document the CLIENT owns, fetched by client_id — not
  // this dossier's. A client with a purchase and two listings saw the same
  // "Help us identify these" pile on all three checklists, so a stray upload on
  // one application nagged them on every other one.
  //
  // Scoped to this dossier, PLUS documents filed nowhere: a per-slot upload
  // carries a dossier, but a bulk-inbox drop has no slot until something files
  // it, so filtering on the dossier alone would leave those unlabelable forever
  // — invisible, with the only screen that can label them refusing to show them.
  // Keyed on the loaded dossier's id, NOT `param` — in link mode `param` is a
  // signed token, not a dossier id. (Link mode is already scoped server-side by
  // the token, so the filter is a no-op there rather than wrong.)
  const belongsHere = useCallback(
    (d: Document) => d.dossier_id == null || d.dossier_id === dossier?.id,
    [dossier?.id],
  );

  const mismatched = documents.filter(
    (d) => d.classification_status === "DECLARED_MISMATCH" && belongsHere(d),
  );
  const unclassified = documents.filter(
    (d) =>
      d.classification_status === "UNCLASSIFIED" &&
      d.status === "PROCESSED" &&
      belongsHere(d),
  );

  // -- render -----------------------------------------------------------------

  if (dossierQuery.isPending) {
    return (
      <div className="dos-page">
        <Skeleton variant="block" style={{ height: 120, marginBottom: 16 }} />
        <Skeleton variant="block" style={{ height: 160, marginBottom: 16 }} />
        <Skeleton variant="block" style={{ height: 320 }} />
      </div>
    );
  }
  if (dossierQuery.isError || !dossier) {
    return (
      <div className="dos-page">
        <header className="dos-head">
          <h1>Document checklist</h1>
          <p>
            {linkMode
              ? "This link is invalid or has expired — ask your agent to send a fresh one."
              : "We couldn't load this dossier. Please try again."}
          </p>
        </header>
      </div>
    );
  }

  // CANCELLED/EXPIRED is over — nothing more is wanted, and an upload into one
  // would sit somewhere nobody is looking.
  //
  // COMPLETE is NOT that. It means every REQUIRED document is in, which says
  // nothing about the OPTIONAL ones: an employer letter or a letter of
  // explanation is very often asked for after the checklist has cleared. Folding
  // COMPLETE in here removed the upload control from the whole screen the moment
  // the dossier finished, so an agent asking for one of those left the client
  // with nowhere to put it. Reported from real use at Checkpoint #19.
  const terminated =
    dossier.status === "CANCELLED" || dossier.status === "EXPIRED";

  const allDossiers: readonly DossierSummary[] = allDossiersQuery.data ?? [];

  return (
    <div className="dos-page">
      {/* Application switcher — lets clients jump between open applications */}
      {!linkMode && allDossiers.length > 0 && (
        <nav className="dos-switcher" aria-label="Your applications">
          {allDossiers.map((d) => {
            const isActive = d.id === dossier.id;
            return (
              <Link
                key={d.id}
                to={`/dossier/${d.id}`}
                className={`dos-switcher__item ${isActive ? "dos-switcher__item--active" : ""}`}
              >
                {d.template_display_name ??
                  d.transaction_type.replace("_", " ")}
                {d.application_number != null && (
                  <span className="dos-switcher__appno">
                    APP-{d.application_number}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      )}

      <header className="dos-head">
        <h1>
          {dossier.template_display_name ?? "Document checklist"}
          {dossier.application_number != null && (
            <span className="dos-head__appno">
              APP-{dossier.application_number}
            </span>
          )}
        </h1>
        <p>
          {dossier.due_date &&
            `Due ${new Date(dossier.due_date).toLocaleDateString()}`}
        </p>
        {/* A mortgage needs more than documents: the borrower's own half of the
            1003 — citizenship, assets, the Section 5 declarations, the credit
            authorization. Nothing else links to it, and a checklist that looks
            finished while those are blank is how an application stalls without
            anyone being told why. Session mode only: the by-link view is for
            uploading documents, and these are attestations that belong to a
            signed-in borrower. */}
        {!linkMode && dossier.transaction_type === "mortgage_application" && (
          <Link
            to="/loan-application"
            className={
              dossier.loan_application_outstanding
                ? "dos-head__apply dos-head__apply--due"
                : "dos-head__apply"
            }
          >
            {dossier.loan_application_outstanding
              ? "Complete your loan application →"
              : "View your loan application →"}
          </Link>
        )}
        {/* Said plainly and next to the checklist, because the checklist is what
            they are looking at. Without this the bar reaches 100%, nothing
            happens, and there is no way to tell that anything is outstanding. */}
        {!linkMode && dossier.loan_application_outstanding && (
          <p className="dos-head__blocker">
            Your documents are only part of this. We still need your loan
            application — the declarations and your permission to check your
            credit — before we can finish and send your paperwork.
          </p>
        )}
      </header>

      {/* checklist progress */}
      <div className="dos-progress">
        <div className="dos-progress__top">
          <span className="dos-progress__title">
            {dossier.status === "COMPLETE"
              ? "All done — thank you!"
              : /* Documents are not the whole requirement for a mortgage. Saying
                   "All set" while the loan application is blank is how a client
                   waits for something that is waiting on them. */
                dossier.loan_application_outstanding
                ? "One thing left"
                : validated === total
                  ? "All set"
                  : "Almost there"}
          </span>
          <span className="dos-progress__count">
            <b>{validated}</b> of <b>{total}</b> validated
          </span>
        </div>
        <div className="dos-bar">
          <div className="dos-bar__fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="dos-progress__chips">
          <span className="dos-mini">
            <span
              className="dos-mini__dot"
              style={{ background: "var(--rp-color-success)" }}
            />
            {validated} validated
          </span>
          <span className="dos-mini">
            <span
              className="dos-mini__dot"
              style={{ background: "var(--rp-color-info)" }}
            />
            {inReview} in review
          </span>
          <span className="dos-mini">
            <span
              className="dos-mini__dot"
              style={{ background: "var(--rp-color-danger)" }}
            />
            {todo} need your attention
          </span>
        </div>
      </div>

      {/* mismatch banner */}
      {mismatched.map((doc) => (
        <MismatchBanner
          key={doc.id}
          doc={doc}
          typeOptions={typeOptions}
          busy={busy}
          onConfirm={() => classify(doc.id, "confirm_declared")}
          onRelabel={(key) => classify(doc.id, "relabel", key)}
          onDiscard={
            linkMode || removalLocked(doc.id)
              ? undefined
              : () => void discard(doc.id, doc.original_filename ?? "Document")
          }
        />
      ))}

      {/* Unclassified labeler — ABOVE the checklist, not below it.
          At the bottom of a ten-slot checklist it sat below the fold: the
          client had answered everything they were asked and still had an
          application that would not progress, with the reason off-screen. It is
          also the only blocking thing on this page, so it goes where blocking
          things go. */}
      {unclassified.length > 0 && (
        <>
          <h2 className="dos-section" style={{ marginTop: 20 }}>
            Help us identify these
          </h2>
          <p className="dos-section-sub">
            We couldn't auto-classify these — pick the right type for each, or
            remove anything you didn't mean to upload.
          </p>
          <div className="dos-unclass">
            {unclassified.map((doc) => (
              <div key={doc.id} className="dos-uc">
                <div className="dos-uc__icon">
                  <FileText
                    size={20}
                    strokeWidth={2}
                    color="var(--rp-color-text-muted)"
                  />
                </div>
                <div>
                  <div className="dos-uc__name">
                    {doc.original_filename ?? "Document"}
                  </div>
                  <div className="dos-uc__conf">
                    {doc.classification_confidence === null
                      ? "No confident match"
                      : `Best guess ${Math.round(Number(doc.classification_confidence) * 100)}% confident`}
                  </div>
                </div>
                <select
                  className="dos-uc__select"
                  defaultValue=""
                  disabled={busy}
                  onChange={(e) => {
                    if (e.target.value)
                      void classify(doc.id, "relabel", e.target.value);
                  }}
                >
                  <option value="">Pick document type…</option>
                  {typeOptions.map(([key, name]) => (
                    <option key={key} value={key}>
                      {name}
                    </option>
                  ))}
                </select>
                {/* The way OUT. Without it the only escape from a mis-drop was
                    to give it a type it isn't. Hidden in link mode — a shared
                    URL is not where destructive actions belong. */}
                {!linkMode && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void discard(doc.id, doc.original_filename ?? "Document")
                    }
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* dropzone — open unless the dossier is CANCELLED/EXPIRED. A COMPLETE
          one still accepts the optional documents an agent asks for after the
          required checklist has cleared. */}
      {!terminated && (
        <div
          className="dos-drop"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            void uploadFiles(e.dataTransfer.files, null);
          }}
        >
          <div className="dos-drop__icon">
            <Upload size={26} strokeWidth={2} color="var(--rp-color-primary)" />
          </div>
          <div className="dos-drop__title">Drop your documents here</div>
          <div className="dos-drop__sub">
            We'll figure out where each one goes — no need to label them.{" "}
            <strong>
              For your ID (driver's license, passport): upload a photo
              (JPG/PNG), not a PDF.
            </strong>
          </div>
          <div className="dos-drop__btn">
            <input
              ref={bulkInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf"
              className="dos-hidden-input"
              onChange={(e) => void uploadFiles(e.target.files, null)}
              aria-label="Browse files to upload"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="dos-hidden-input"
              onChange={(e) => void uploadFiles(e.target.files, null)}
              aria-label="Take a photo to upload"
            />
            <input
              ref={slotInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="dos-hidden-input"
              onChange={(e) =>
                void uploadFiles(e.target.files, targetSlotRef.current)
              }
              aria-label="Upload into the selected slot"
            />
            <button
              type="button"
              className="rp-btn rp-btn--primary rp-btn--md"
              disabled={busy}
              onClick={() => bulkInputRef.current?.click()}
            >
              Browse files
            </button>
            <button
              type="button"
              className="rp-btn rp-btn--secondary rp-btn--md dos-camera-btn"
              disabled={busy}
              onClick={() => cameraInputRef.current?.click()}
            >
              <Camera size={16} strokeWidth={2} /> Take a photo
            </button>
          </div>
        </div>
      )}

      {/* per-file upload progress */}
      {pendingUploads.length > 0 && (
        <div className="dos-upload-panel">
          {pendingUploads.map((u, i) => (
            <div key={i} className="dos-upload-row">
              <div
                className={`dos-upload-row__icon${u.status === "uploading" ? " dos-slot__icon--spin" : ""}`}
              >
                {u.status === "uploading" && (
                  <Loader2
                    size={14}
                    strokeWidth={2.2}
                    color="var(--rp-color-info)"
                  />
                )}
                {u.status === "done" && (
                  <Check
                    size={14}
                    strokeWidth={2.4}
                    color="var(--rp-color-success)"
                  />
                )}
                {u.status === "error" && (
                  <AlertTriangle
                    size={14}
                    strokeWidth={2.2}
                    color="var(--rp-color-danger)"
                  />
                )}
              </div>
              <span className="dos-upload-row__name">{u.name}</span>
              <span
                className={`dos-upload-row__status dos-upload-row__status--${u.status}`}
              >
                {u.status === "uploading"
                  ? "Uploading…"
                  : u.status === "done"
                    ? "Uploaded"
                    : "Failed"}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* slot checklist */}
      <h2 className="dos-section">Document checklist</h2>
      <p className="dos-section-sub">Everything we need for your file.</p>
      <div className="dos-slots">
        {slotViews.map(({ slot, state }) => {
          const meta = STATE_META[state];
          // "Needed" is a demand, and an optional document is not one. A slot
          // nobody has to fill said the same word as the six that block the
          // dossier, so the checklist read as though the client were behind on
          // something they had never been asked for.
          const pillLabel =
            state === "needed" && !slot.is_required ? "Optional" : meta.label;
          const Glyph = meta.Glyph;
          return (
            <div
              key={slot.id}
              className="dos-slot"
              style={
                {
                  "--dos-accent": meta.accent,
                  "--dos-accent-soft": meta.soft,
                } as CSSProperties
              }
            >
              <div
                className={`dos-slot__icon ${meta.spin ? "dos-slot__icon--spin" : ""}`}
              >
                <Glyph size={18} strokeWidth={2.2} color={meta.accent} />
              </div>
              <div className="dos-slot__body">
                <div className="dos-slot__role">
                  {slot.display_name}
                  {!slot.is_required && " (optional)"}
                </div>
                <div className="dos-slot__meta">
                  {slot.recency_days
                    ? `Within the last ${slot.recency_days} days`
                    : "Any recent copy works"}
                </div>
                {state === "rejected" && slot.rejection_reason && (
                  <div className="dos-slot__reason">
                    <AlertTriangle size={13} strokeWidth={2.4} />{" "}
                    {slot.rejection_reason}
                  </div>
                )}
              </div>
              <div className="dos-slot__right">
                <StatusPill tone={meta.tone}>{pillLabel}</StatusPill>
                {/* Any slot with a document behind it can be opened — including
                    a rejected one, where seeing WHAT was sent is usually how
                    the client works out what went wrong. */}
                {!linkMode && slot.document_id && (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={viewingDocId === slot.document_id}
                    onClick={() => void viewDocument(slot.document_id!)}
                  >
                    {viewingDocId === slot.document_id ? "Opening…" : "View"}
                  </Button>
                )}
                {/* Anything not already satisfied can be (re-)uploaded.
                    It used to be `needed` or `rejected` ONLY, which left a slot
                    whose document never finished classifying showing a View
                    button and nothing else — the client could see the file they
                    sent and had no way to send another. Reported from real use:
                    "I don't see upload option, it has only view".
                    `processing` and `in_review` are exactly the states where a
                    client may realise they sent the wrong thing, and a slot
                    stuck in one is otherwise a dead end. Only `validated`
                    withholds it: that requirement is met, and replacing it would
                    quietly un-satisfy a dossier that may already have completed. */}
                {/* A form the client fills in THEMSELVES — today only TREC
                    55-1, whose ~60 questions about the property's condition
                    only the owner can answer (V091). They cannot upload one
                    until they have a blank one, and "upload your seller's
                    disclosure" with nowhere to get it is a dead end. */}
                {BLANK_FORM_BY_DOC_TYPE[slot.doc_type_key] && (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={downloadingBlank === slot.doc_type_key}
                    onClick={() => void downloadBlank(slot.doc_type_key)}
                  >
                    {downloadingBlank === slot.doc_type_key
                      ? "Opening…"
                      : "Download blank form"}
                  </Button>
                )}
                {/* A finished dossier still accepts the documents it never
                    required — that is the whole point of "optional". It does
                    not accept a required one: those are all satisfied by
                    definition of completion, and offering an upload that the
                    API will refuse is a dead end with a spinner on the end of
                    it. Defence in depth — the API decides, this only avoids
                    offering what it will refuse.
                    Which slot statuses take an upload is the shared
                    slot-actions contract (spec 019): an in-review document can
                    be replaced, an accepted one cannot. */}
                {/* Take back what was sent (spec 017). A required item of a
                    finished application is excluded: completion means every
                    required item was satisfied, and a finished application
                    accepts no upload into one — freeing it would strand a
                    "Needed" item nobody can fill. The API refuses it too.
                    Spec 019: an ACCEPTED document (validated or agent-
                    approved, on any row) is locked for the client — only the
                    agent's "Mark as mistake…" undoes it. */}
                {!linkMode &&
                  !terminated &&
                  slot.document_id &&
                  !removalLocked(slot.document_id) &&
                  !(dossier.status === "COMPLETE" && slot.is_required) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        void discard(slot.document_id!, slot.display_name)
                      }
                    >
                      Remove
                    </Button>
                  )}
                {!terminated &&
                  slotActions(slot.status).client_upload &&
                  !(dossier.status === "COMPLETE" && slot.is_required) && (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      onClick={() => pickForSlot(slot)}
                    >
                      {slot.document_id ? "Upload replacement" : "Upload"}
                    </Button>
                  )}
              </div>
            </div>
          );
        })}
      </div>

      <Toast
        message={toast}
        onDismiss={() => setToast(null)}
        icon={<Check size={14} strokeWidth={2.4} />}
        duration={2400}
      />
    </div>
  );
}

function hasInFlight(dossier: Dossier | undefined): boolean {
  if (!dossier) return false;
  return dossier.slots.some(
    (slot) =>
      slotState(slot) === "processing" || slotState(slot) === "in_review",
  );
}

function MismatchBanner({
  doc,
  typeOptions,
  busy,
  onConfirm,
  onRelabel,
  onDiscard,
}: {
  doc: Document;
  typeOptions: readonly (readonly [string, string])[];
  busy: boolean;
  onConfirm: () => void;
  onRelabel: (docTypeKey: string) => void;
  /** Absent on a signed link: no destructive action on a shared URL. */
  onDiscard?: () => void;
}) {
  const declared =
    typeOptions.find(([key]) => key === doc.doc_type)?.[1] ??
    doc.doc_type ??
    "that type";
  return (
    <div className="dos-mismatch" role="alert">
      <div className="dos-mismatch__icon">
        <AlertTriangle
          size={18}
          strokeWidth={2.2}
          color="var(--rp-color-warning)"
        />
      </div>
      <div className="dos-mismatch__body">
        <div className="dos-mismatch__title">
          "{doc.original_filename ?? "A document"}" doesn't look like a{" "}
          {declared}
        </div>
        <div className="dos-mismatch__sub">
          If it really is, confirm below — otherwise tell us what it actually
          is.
        </div>
      </div>
      <div className="dos-mismatch__actions">
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={onConfirm}
        >
          It really is my {declared}
        </Button>
        <select
          className="dos-uc__select"
          defaultValue=""
          disabled={busy}
          onChange={(e) => {
            if (e.target.value) onRelabel(e.target.value);
          }}
        >
          <option value="">It's actually a…</option>
          {typeOptions
            .filter(([key]) => key !== doc.doc_type)
            .map(([key, name]) => (
              <option key={key} value={key}>
                {name}
              </option>
            ))}
        </select>
        {/* The way out the card never had: the client knows it is simply the
            wrong file (spec 017). */}
        {onDiscard && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={onDiscard}>
            I uploaded this by mistake
          </Button>
        )}
      </div>
    </div>
  );
}
